"use client";

import type { ReactNode } from "react";
import type {
  TradeOrder,
  VybeDealInternal,
  VybeOrderInternal,
} from "@/vybe-trader-client/trade-history/types";
import {
  dealOpenTimeMs,
  formatDealType,
  formatDirection,
  formatExpirationType,
  formatFillPolicy,
  formatOrderState,
  formatOrderType,
  formatPlacedType,
  VybeOrderType,
} from "@/vybe-trader-client/trade-history/types";
import {
  formatHistoryLocalDateTime,
  formatHistoryLocalDateTimeMs,
} from "@/components/history-view/historyDateTimeFormat";
import { useSettingsStore } from "@/lib/settings/store";

export type HistoryDetailKind = "position" | "order" | "deal";

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={`text-sm font-medium text-foreground break-all ${
          valueClassName ?? ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-medium text-muted-foreground mt-4 mb-2 first:mt-0">
      {children}
    </p>
  );
}

function signedClass(n: number): string | undefined {
  if (!Number.isFinite(n)) return undefined;
  return n >= 0 ? "text-blue-400" : "text-red-400";
}

function fmt(n: unknown, digits = 5): string {
  if (typeof n === "number" && Number.isFinite(n)) return n.toFixed(digits);
  if (n === null || n === undefined) return "—";
  return String(n);
}

function fmtAny(n: unknown): string {
  if (n === null || n === undefined) return "—";
  if (typeof n === "boolean") return n ? "Yes" : "No";
  if (typeof n === "number" && Number.isFinite(n)) return String(n);
  return String(n);
}

function fmtTimeMs(ms: number): string {
  return formatHistoryLocalDateTimeMs(ms);
}

function fmtTimeString(s: string | null | undefined): string {
  if (!s) return "—";
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return s;
  return formatHistoryLocalDateTime(t);
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined) {
    return <DetailRow label={label} value="—" />;
  }
  return (
    <div className="py-2 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <pre className="mt-1 text-xs font-mono whitespace-pre-wrap break-all text-foreground max-h-48 overflow-y-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

export type HistoryDetailsContentProps =
  | { kind: "position"; data: TradeOrder }
  | { kind: "order"; data: VybeOrderInternal }
  | { kind: "deal"; data: VybeDealInternal };

export default function HistoryDetailsContent(props: HistoryDetailsContentProps) {
  if (props.kind === "position") {
    const p = props.data;
    const isBalance =
      p.orderType === VybeOrderType.Balance || p.orderType === VybeOrderType.Credit;

    // Standard (default): the compact MT5-style card — the extended dump below (incl. raw nested
    // JSON) is opt-in via Settings → Extended history details (SR Global request 2026-09-08).
    if (!useSettingsStore.getState().settings.dealDetailsExtended) {
      const px = Number.isFinite(p.digits) && p.digits > 0 ? p.digits : p.openPrice >= 20 ? 3 : 5;
      const charges = (p.commission ?? 0) + (p.fee ?? 0);
      const hasSl = Number.isFinite(p.stopLoss) && p.stopLoss > 0;
      const hasTp = Number.isFinite(p.takeProfit) && p.takeProfit > 0;
      const lots = p.lots || p.volume || 0;
      const closed = Number.isFinite(p.closePrice) && p.closePrice > 0;
      return (
        <div className="pr-1">
          <div className="flex items-start justify-between gap-3">
            <div className="text-base font-semibold text-foreground">
              {p.symbol || "—"}{" "}
              <span className={p.orderType === VybeOrderType.Buy ? "text-blue-400" : "text-red-400"}>
                {formatOrderType(p.orderType).toLowerCase()}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">#{p.ticket}</span>
          </div>
          {isBalance ? (
            <div className="mt-3 text-2xl font-light text-foreground">{fmt(p.profit, 2)}</div>
          ) : (
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-2xl font-light text-foreground">
                {fmt(lots, 2)} at {fmt(p.openPrice, px)}{closed ? ` → ${fmt(p.closePrice, px)}` : ""}
              </span>
              <span className={`text-2xl font-medium ${signedClass(p.profit) ?? ""}`}>
                {p.profit >= 0 ? "+" : ""}{fmt(p.profit, 2)}
              </span>
            </div>
          )}
          <div className="mt-2 text-sm text-muted-foreground">
            {fmtTimeString(p.openTime)}{closed && p.closeTime ? ` → ${fmtTimeString(p.closeTime)}` : ""}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Position</span><span className="text-foreground">{p.ticket}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Swap</span><span className={signedClass(p.swap) ?? ""}>{p.swap ? fmt(p.swap, 2) : "-"}</span></div>
            {hasSl && <div className="flex justify-between"><span className="text-muted-foreground">S/L</span><span className="text-foreground">{fmt(p.stopLoss, px)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Charges</span><span className={signedClass(charges) ?? ""}>{charges ? fmt(charges, 2) : "-"}</span></div>
            {hasTp && <div className="flex justify-between"><span className="text-muted-foreground">T/P</span><span className="text-foreground">{fmt(p.takeProfit, px)}</span></div>}
          </div>
          {p.comment ? <div className="mt-3 text-xs text-muted-foreground">{p.comment}</div> : null}
          <p className="mt-4 text-xs text-muted-foreground/70">Every field: Settings → Extended history details.</p>
        </div>
      );
    }

    return (
      <div className="pr-1">
        <SectionTitle>Identification</SectionTitle>
        <DetailRow label="Ticket" value={String(p.ticket)} />
        <DetailRow label="Symbol" value={p.symbol || "—"} />
        <DetailRow
          label="Order type (raw / label)"
          value={`${p.orderType} — ${formatOrderType(p.orderType)}`}
        />
        <DetailRow
          label="Deal type (raw / label)"
          value={`${p.dealType} — ${formatDealType(p.dealType)}`}
        />
        <DetailRow
          label="State (raw / label)"
          value={`${p.state} — ${formatOrderState(p.state)}`}
        />
        <DetailRow
          label="Placed type (raw / label)"
          value={`${p.placedType} — ${formatPlacedType(p.placedType)}`}
        />

        <SectionTitle>Financials</SectionTitle>
        <DetailRow
          label="Profit"
          value={`${p.profit >= 0 ? "+" : ""}${fmt(p.profit, 2)}`}
          valueClassName={signedClass(p.profit)}
        />
        <DetailRow
          label="Swap"
          value={fmt(p.swap, 2)}
          valueClassName={signedClass(p.swap)}
        />
        <DetailRow
          label="Commission"
          value={fmt(p.commission, 2)}
          valueClassName={signedClass(p.commission)}
        />
        <DetailRow
          label="Fee"
          value={fmt(p.fee, 2)}
          valueClassName={signedClass(p.fee)}
        />
        <DetailRow label="Profit rate" value={fmt(p.profitRate, 5)} />

        <SectionTitle>Open</SectionTitle>
        <DetailRow label="Open time" value={fmtTimeString(p.openTime)} />
        <DetailRow
          label="Open timestamp (UTC ms)"
          value={fmtTimeMs(p.openTimestampUTC)}
        />
        <DetailRow label="Open price" value={fmt(p.openPrice, p.digits)} />
        <DetailRow label="Lots" value={fmt(p.lots, 2)} />
        <DetailRow label="Volume" value={fmt(p.volume, 2)} />
        <DetailRow label="Contract size" value={fmt(p.contractSize, 2)} />
        <DetailRow label="Digits" value={String(p.digits)} />

        <SectionTitle>Close</SectionTitle>
        <DetailRow label="Close time" value={fmtTimeString(p.closeTime)} />
        <DetailRow
          label="Close timestamp (UTC ms)"
          value={fmtTimeMs(p.closeTimestampUTC)}
        />
        <DetailRow label="Close price" value={fmt(p.closePrice, p.digits)} />
        <DetailRow label="Close lots" value={fmt(p.closeLots, 2)} />
        <DetailRow label="Close volume" value={fmt(p.closeVolume, 2)} />
        <DetailRow label="Close comment" value={p.closeComment || "—"} />

        <SectionTitle>Stops &amp; limits</SectionTitle>
        <DetailRow label="Stop loss" value={fmt(p.stopLoss, p.digits)} />
        <DetailRow label="Take profit" value={fmt(p.takeProfit, p.digits)} />
        <DetailRow label="Stop limit price" value={fmt(p.stopLimitPrice, p.digits)} />
        <DetailRow
          label="Expiration type (raw / label)"
          value={`${p.expirationType} — ${formatExpirationType(p.expirationType)}`}
        />
        <DetailRow label="Expiration time" value={fmtTimeString(p.expirationTime)} />
        <DetailRow
          label="Fill policy (raw / label)"
          value={`${p.fillPolicy} — ${formatFillPolicy(p.fillPolicy)}`}
        />

        <SectionTitle>Other</SectionTitle>
        <DetailRow label="Expert id" value={String(p.expertId)} />
        <DetailRow label="Request id" value={String(p.requestId)} />
        <DetailRow label="Comment" value={p.comment || "—"} />

        {!isBalance && p.openPrice !== 0 && Number.isFinite(p.openPrice) && (
          <DetailRow
            label="Price move (close − open)"
            value={fmt(p.closePrice - p.openPrice, p.digits)}
            valueClassName={signedClass(p.closePrice - p.openPrice)}
          />
        )}

        <SectionTitle>Nested objects</SectionTitle>
        <JsonBlock label="dealInternalIn" value={p.dealInternalIn} />
        <JsonBlock label="dealInternalOut" value={p.dealInternalOut} />
        <JsonBlock label="orderInternal" value={p.orderInternal} />
      </div>
    );
  }

  if (props.kind === "order") {
    const o = props.data;

    // Standard (default): compact card; the raw/flag dump below is opt-in via Settings.
    if (!useSettingsStore.getState().settings.dealDetailsExtended) {
      const price = o.price || o.openPrice || 0;
      const px = Number.isFinite(o.digits) && o.digits > 0 ? o.digits : price >= 20 ? 3 : 5;
      const hasSl = Number.isFinite(o.stopLoss) && o.stopLoss > 0;
      const hasTp = Number.isFinite(o.takeProfit) && o.takeProfit > 0;
      const lots = o.lots || o.volume || o.requestLots || 0;
      const when = o.executionTime ? fmtTimeMs(o.executionTime * 1000) : fmtTimeMs(o.openTimeMs);
      return (
        <div className="pr-1">
          <div className="flex items-start justify-between gap-3">
            <div className="text-base font-semibold text-foreground">
              {o.symbol || "—"}{" "}
              <span className={o.isBuyOrder ? "text-blue-400" : "text-red-400"}>
                {formatOrderType(o.type).toLowerCase()}
              </span>
              <span className="ml-2 text-sm font-normal text-muted-foreground">{formatOrderState(o.state).toLowerCase()}</span>
            </div>
            <span className="text-sm text-muted-foreground">#{o.ticketNumber || o.ticket}</span>
          </div>
          <div className="mt-3 text-2xl font-light text-foreground">
            {fmt(lots, 2)} at {fmt(price, px)}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">{when}</div>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="text-foreground">{o.ticketNumber || o.ticket}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Deal</span><span className="text-foreground">{o.dealTicket || "-"}</span></div>
            {hasSl && <div className="flex justify-between"><span className="text-muted-foreground">S/L</span><span className="text-foreground">{fmt(o.stopLoss, px)}</span></div>}
            {hasTp && <div className="flex justify-between"><span className="text-muted-foreground">T/P</span><span className="text-foreground">{fmt(o.takeProfit, px)}</span></div>}
          </div>
          {o.comment ? <div className="mt-3 text-xs text-muted-foreground">{o.comment}</div> : null}
          <p className="mt-4 text-xs text-muted-foreground/70">Every field: Settings → Extended history details.</p>
        </div>
      );
    }

    return (
      <div className="pr-1">
        <SectionTitle>Identification</SectionTitle>
        <DetailRow label="Ticket number" value={String(o.ticketNumber)} />
        <DetailRow label="Ticket" value={String(o.ticket)} />
        <DetailRow label="Id" value={o.id || "—"} />
        <DetailRow label="Login" value={String(o.login)} />
        <DetailRow label="Symbol" value={o.symbol || "—"} />

        <SectionTitle>Classification</SectionTitle>
        <DetailRow
          label="Type (raw / label)"
          value={`${o.type} — ${formatOrderType(o.type)}`}
        />
        <DetailRow
          label="State (raw / label)"
          value={`${o.state} — ${formatOrderState(o.state)}`}
        />
        <DetailRow
          label="Placed type (raw / label)"
          value={`${o.placedType} — ${formatPlacedType(o.placedType)}`}
        />
        <DetailRow
          label="Fill policy (raw / label)"
          value={`${o.fillPolicy} — ${formatFillPolicy(o.fillPolicy)}`}
        />
        <DetailRow
          label="Expiration type (raw / label)"
          value={`${o.expirationType} — ${formatExpirationType(o.expirationType)}`}
        />

        <SectionTitle>Flags</SectionTitle>
        <DetailRow label="isAssociativeDealOrder" value={fmtAny(o.isAssociativeDealOrder)} />
        <DetailRow label="isTradeOrder" value={fmtAny(o.isTradeOrder)} />
        <DetailRow label="isLimitOrder" value={fmtAny(o.isLimitOrder)} />
        <DetailRow label="isStopOrder" value={fmtAny(o.isStopOrder)} />
        <DetailRow label="isBuyOrder" value={fmtAny(o.isBuyOrder)} />
        <DetailRow label="isStopLimitOrder" value={fmtAny(o.isStopLimitOrder)} />

        <SectionTitle>Times</SectionTitle>
        <DetailRow label="History time" value={fmtTimeMs(o.historyTime)} />
        <DetailRow label="Open time (raw)" value={String(o.openTime)} />
        <DetailRow label="Open time (ms)" value={fmtTimeMs(o.openTimeMs)} />
        <DetailRow label="Open time (ms, ISO string)" value={o.openTimeMsAsDateTime || "—"} />
        <DetailRow label="Expiration time" value={fmtTimeMs(o.expirationTime)} />
        <DetailRow label="Execution time (sec → local)" value={fmtTimeMs(o.executionTime * 1000)} />
        <DetailRow label="Execution time (raw string)" value={o.executionTimeAsDateTime || "—"} />

        <SectionTitle>Prices &amp; volume</SectionTitle>
        <DetailRow label="Open price" value={fmt(o.openPrice, o.digits)} />
        <DetailRow label="Stop limit price" value={fmt(o.stopLimitPrice, o.digits)} />
        <DetailRow label="Price" value={fmt(o.price, o.digits)} />
        <DetailRow label="Stop loss" value={fmt(o.stopLoss, o.digits)} />
        <DetailRow label="Take profit" value={fmt(o.takeProfit, o.digits)} />
        <DetailRow label="Lots" value={fmt(o.lots, 2)} />
        <DetailRow label="Volume" value={fmt(o.volume, 2)} />
        <DetailRow label="Request lots" value={fmt(o.requestLots, 2)} />
        <DetailRow label="Request volume" value={fmt(o.requestVolume, 2)} />

        <SectionTitle>Other</SectionTitle>
        <DetailRow label="Expert id" value={String(o.expertId)} />
        <DetailRow label="Deal ticket" value={String(o.dealTicket)} />
        <DetailRow label="Comment" value={o.comment || "—"} />
        <DetailRow label="Contract size" value={fmt(o.contractSize, 2)} />
        <DetailRow label="Digits" value={String(o.digits)} />
        <DetailRow label="Base digits" value={String(o.baseDigits)} />
        <DetailRow label="Profit rate" value={fmt(o.profitRate, 5)} />
      </div>
    );
  }

  const d = props.data;
  const openMs = dealOpenTimeMs(d);

  // Standard (default) = the compact MT5-style card the client asked for: volume @ price, P&L,
  // time, deal/order/position ids, SL/TP when set, swap and charges. Extended (Settings toggle)
  // = every raw field below, kept for testing — nothing is hidden, only de-emphasised.
  if (!useSettingsStore.getState().settings.dealDetailsExtended) {
    const charges = (d.commission ?? 0) + (d.fee ?? 0);
    const hasSl = Number.isFinite(d.stopLoss) && d.stopLoss > 0;
    const hasTp = Number.isFinite(d.takeProfit) && d.takeProfit > 0;
    // The gateway fills `lots` (volume stays 0) and may send digits=0 — mirror the list row:
    // lots first, and infer price precision when the symbol spec didn't come through.
    const lots = d.lots || d.volume || 0;
    const px = Number.isFinite(d.digits) && d.digits > 0 ? d.digits : d.price >= 20 ? 3 : 5;
    return (
      <div className="pr-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-foreground">
              {d.symbol || "—"}{" "}
              <span className={d.type === 0 ? "text-blue-400" : "text-red-400"}>
                {formatDealType(d.type).toLowerCase()}, {formatDirection(d.direction).toLowerCase()}
              </span>
            </div>
          </div>
          <span className="text-sm text-muted-foreground">#{d.ticketNumber}</span>
        </div>
        <div className="mt-3 flex items-baseline justify-between">
          <span className="text-2xl font-light text-foreground">
            {fmt(lots, 2)} at {fmt(d.price, px)}
          </span>
          <span className={`text-2xl font-medium ${signedClass(d.profit) ?? ""}`}>
            {d.profit >= 0 ? "+" : ""}{fmt(d.profit, 2)}
          </span>
        </div>
        <div className="mt-2 text-sm text-muted-foreground">{fmtTimeMs(openMs)}</div>
        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Deal</span><span className="text-foreground">{d.ticketNumber}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Swap</span><span className={signedClass(d.swap) ?? ""}>{d.swap ? fmt(d.swap, 2) : "-"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="text-foreground">{d.orderTicket || "-"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Charges</span><span className={signedClass(charges) ?? ""}>{charges ? fmt(charges, 2) : "-"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Position</span><span className="text-foreground">{d.positionTicket || "-"}</span></div>
          {hasSl && <div className="flex justify-between"><span className="text-muted-foreground">S/L</span><span className="text-foreground">{fmt(d.stopLoss, px)}</span></div>}
          {hasTp && <div className="flex justify-between"><span className="text-muted-foreground">T/P</span><span className="text-foreground">{fmt(d.takeProfit, px)}</span></div>}
        </div>
        {d.comment ? <div className="mt-3 text-xs text-muted-foreground">{d.comment}</div> : null}
        <p className="mt-4 text-xs text-muted-foreground/70">Every field: Settings → Extended history details.</p>
      </div>
    );
  }

  return (
    <div className="pr-1">
      <SectionTitle>Identification</SectionTitle>
      <DetailRow label="Ticket number" value={String(d.ticketNumber)} />
      <DetailRow label="Id" value={d.id || "—"} />
      <DetailRow label="Login" value={String(d.login)} />
      <DetailRow label="Symbol" value={d.symbol || "—"} />
      <DetailRow label="Order ticket" value={String(d.orderTicket)} />
      <DetailRow label="Position ticket" value={String(d.positionTicket)} />

      <SectionTitle>Classification</SectionTitle>
      <DetailRow
        label="Type (raw / label)"
        value={`${d.type} — ${formatDealType(d.type)}`}
      />
      <DetailRow
        label="Direction (raw / label)"
        value={`${d.direction} — ${formatDirection(d.direction)}`}
      />
      <DetailRow
        label="Placed type (raw / label)"
        value={`${d.placedType} — ${formatPlacedType(d.placedType)}`}
      />

      <SectionTitle>Financials</SectionTitle>
      <DetailRow
        label="Profit"
        value={`${d.profit >= 0 ? "+" : ""}${fmt(d.profit, 2)}`}
        valueClassName={signedClass(d.profit)}
      />
      <DetailRow
        label="Commission"
        value={fmt(d.commission, 2)}
        valueClassName={signedClass(d.commission)}
      />
      <DetailRow
        label="Fee"
        value={fmt(d.fee, 2)}
        valueClassName={signedClass(d.fee)}
      />
      <DetailRow
        label="Swap"
        value={fmt(d.swap, 2)}
        valueClassName={signedClass(d.swap)}
      />
      <DetailRow label="Profit rate" value={fmt(d.profitRate, 5)} />
      <DetailRow label="Volume rate" value={fmt(d.volumeRate, 5)} />
      <DetailRow label="Free profit" value={fmt(d.freeProfit, 2)} />

      <SectionTitle>Times</SectionTitle>
      <DetailRow label="History time" value={fmtTimeMs(d.historyTime)} />
      <DetailRow label="Open time (raw)" value={String(d.openTime)} />
      <DetailRow label="Open time (ms)" value={fmtTimeMs(openMs)} />
      <DetailRow label="Open time (DateTime string)" value={d.openTimeAsDateTime || "—"} />

      <SectionTitle>Prices &amp; volume</SectionTitle>
      <DetailRow label="Open price" value={fmt(d.openPrice, d.digits)} />
      <DetailRow label="Price" value={fmt(d.price, d.digits)} />
      <DetailRow label="Stop loss" value={fmt(d.stopLoss, d.digits)} />
      <DetailRow label="Take profit" value={fmt(d.takeProfit, d.digits)} />
      <DetailRow label="Lots" value={fmt(d.lots, 2)} />
      <DetailRow label="Volume" value={fmt(d.volume, 2)} />

      <SectionTitle>Contract</SectionTitle>
      <DetailRow label="Contract size" value={fmt(d.contractSize, 2)} />
      <DetailRow label="Digits" value={String(d.digits)} />
      <DetailRow label="Money digits" value={String(d.moneyDigits)} />
      <DetailRow label="Trail rounder" value={String(d.trailRounder)} />

      <SectionTitle>Other</SectionTitle>
      <DetailRow label="Expert id" value={String(d.expertId)} />
      <DetailRow label="Comment" value={d.comment || "—"} />
    </div>
  );
}
